import React from "react";
import styled from "styled-components";

import { ButtonsRow, FooterNote } from "../../Common/Components/FormComponents";
import { IStyle } from "../../../Types/Style";
import {
  buildObjFrom,
  defaultColors,
  IColor,
  ILessVariable,
  interpolateSelectableColors,
  LessVarKeys,
  mapColorToLessVariable,
  parseSelectableColors,
  SelectableColors,
} from "../helpers/colorParser";
import { AddButton, WarnButton } from "./common/buttons";

interface IBrandColors {
  style: IStyle;
  modifyLessCode: (lessCode: string) => void;
  toggleMode: () => void;
}

const Note = styled(FooterNote)`
  display: block;
`;

const displayColor = (color: IColor): React.ReactNode => (
  <div key={color.title}>
    <h4>{color.title}</h4>
    <Note>{color.description}</Note>
    <input data-testid={color.title} defaultValue={color.color} ref={color.ref} type="color" />
  </div>
);

const mapInputRefsToLessVariables = (colors: SelectableColors<IColor>): SelectableColors<ILessVariable> =>
  buildObjFrom(key => mapColorToLessVariable(key, colors[key], defaultColors[key]));

interface IColorInput {
  description: string;
  ref: React.RefObject<HTMLInputElement>;
  title: string;
}

const colorInputs: SelectableColors<IColorInput> = {
  [LessVarKeys.ACCENT_COLOUR]: {
    description: "This colour will apply to the main navigation accent",
    ref: React.createRef(),
    title: "Accent Colour",
  },
  [LessVarKeys.HEADER_COLOUR]: {
    description: "This colour will apply to the main navigation header",
    ref: React.createRef(),
    title: "Header Colour",
  },
  [LessVarKeys.HEADER_BUTTON_NORMAL]: {
    description: "This colour will apply to the buttons in the main navigation header",
    ref: React.createRef(),
    title: "Header Buttons (normal)",
  },
  [LessVarKeys.HEADER_BUTTON_HOVER]: {
    description: "This colour will apply to the buttons in the main navigation header",
    ref: React.createRef(),
    title: "Header Buttons (hover)",
  },
  [LessVarKeys.PROGRESS_BAR]: {
    description: "This colour will apply to the progress bar in the main navigation header from the learner app",
    ref: React.createRef(),
    title: "Progress Bar",
  },
  [LessVarKeys.NAVIGATION_BUTTON]: {
    description:
      "This colour will apply to the navigation buttons that appear in the course, e.g. CONTINUE, RETURN, ANSWER, etc",
    ref: React.createRef(),
    title: "Navigation Buttons",
  },
};

const BrandColors: React.FC<IBrandColors> = props => {
  const { style } = props;
  const selectableColors = parseSelectableColors(style.lessCode);
  const colors: SelectableColors<IColor> = buildObjFrom(key => ({
    color: selectableColors[key].value,
    ...colorInputs[key],
  }));
  const modifyLessCode = () => {
    props.modifyLessCode(interpolateSelectableColors(style.lessCode, mapInputRefsToLessVariables(colors)));
  };

  return (
    <>
      <h2>Brand Colours</h2>
      {Object.values(colors).map(displayColor)}
      <ButtonsRow margin={1} width={42}>
        <AddButton onClick={modifyLessCode}>Save Colours</AddButton>
        <WarnButton onClick={props.toggleMode}>Advanced theme settings</WarnButton>
      </ButtonsRow>
    </>
  );
};

export default BrandColors;
